type TrendBand = 'falling' | 'flat' | 'rising';
type DensityBand = 'sparse' | 'balanced' | 'dense';
type TextureBand = 'smooth' | 'textured' | 'turbulent';

interface EncodedState {
  id: string;
  label: string;
}

export interface MarkovTransitionPrediction {
  id: string;
  label: string;
  probability: number;
  count: number;
}

export interface MarkovTransitionForecast {
  currentStateId: string;
  currentStateLabel: string;
  sampleSize: number;
  predictions: MarkovTransitionPrediction[];
}

interface MarkovOptions {
  cells?: number;
  generations?: number;
  topK?: number;
  alpha?: number;
  horizon?: number;
  immediateWeight?: number;
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const TRENDS: TrendBand[] = ['falling', 'flat', 'rising'];
const DENSITIES: DensityBand[] = ['sparse', 'balanced', 'dense'];
const TEXTURES: TextureBand[] = ['smooth', 'textured', 'turbulent'];

const STATE_SPACE: EncodedState[] = TRENDS.flatMap((trend) =>
  DENSITIES.flatMap((densityBand) =>
    TEXTURES.map((textureBand) => ({
      id: `${trend}|${densityBand}|${textureBand}`,
      label: `${trend} ${densityBand} / ${textureBand}`,
    })),
  ),
);

const toTrendBand = (deltaDensity: number): TrendBand => {
  if (deltaDensity > 0.03) return 'rising';
  if (deltaDensity < -0.03) return 'falling';
  return 'flat';
};

const toDensityBand = (density: number): DensityBand => {
  if (density < 0.18) return 'sparse';
  if (density > 0.42) return 'dense';
  return 'balanced';
};

const toTextureBand = (edgeRatio: number): TextureBand => {
  if (edgeRatio < 0.2) return 'smooth';
  if (edgeRatio > 0.5) return 'turbulent';
  return 'textured';
};

const encodeState = (row: number[], previousDensity: number): EncodedState => {
  const activeCells = row.reduce((sum, value) => sum + (value === 1 ? 1 : 0), 0);
  const density = activeCells / row.length;

  let edgeCount = 0;
  for (let i = 1; i < row.length; i++) {
    if (row[i] !== row[i - 1]) {
      edgeCount += 1;
    }
  }

  const edgeRatio = row.length > 1 ? edgeCount / (row.length - 1) : 0;
  const trend = toTrendBand(density - previousDensity);
  const densityBand = toDensityBand(density);
  const textureBand = toTextureBand(edgeRatio);

  return {
    id: `${trend}|${densityBand}|${textureBand}`,
    label: `${trend} ${densityBand} / ${textureBand}`,
  };
};

const applyRule = (left: number, center: number, right: number, ruleset: number[]) => {
  const pattern = (left << 2) | (center << 1) | right;
  const ruleIndex = 7 - pattern;
  return ruleset[ruleIndex] ?? 0;
};

const evolveRow = (row: number[], ruleset: number[]) => {
  const nextRow = Array(row.length).fill(0);

  for (let i = 1; i < row.length - 1; i++) {
    nextRow[i] = applyRule(row[i - 1], row[i], row[i + 1], ruleset);
  }

  return nextRow;
};

const buildRows = (ruleset: number[], cells: number, generations: number) => {
  const rows: number[][] = [];
  const initial = Array(cells).fill(0);
  initial[Math.floor(cells / 2)] = 1;
  rows.push(initial);

  for (let i = 1; i < generations; i++) {
    rows.push(evolveRow(rows[i - 1], ruleset));
  }

  return rows;
};

const multiplyVectorByMatrix = (vector: number[], matrix: number[][]) => {
  const width = matrix[0]?.length ?? 0;
  const out = Array(width).fill(0);

  for (let i = 0; i < vector.length; i++) {
    const value = vector[i];
    if (!value) continue;
    const row = matrix[i];
    if (!row) continue;
    for (let j = 0; j < row.length; j++) {
      out[j] += value * row[j];
    }
  }

  return out;
};

const normalize = (vector: number[]) => {
  const total = vector.reduce((sum, value) => sum + value, 0);
  if (total <= 0) return vector.map(() => 0);
  return vector.map((value) => value / total);
};

export const buildMarkovTransitionForecast = (
  rawRuleset: number[],
  options: MarkovOptions = {},
): MarkovTransitionForecast | null => {
  if (rawRuleset.length < 8) return null;

  const cells = Math.max(32, options.cells ?? 84);
  const generations = Math.max(24, options.generations ?? 96);
  const topK = Math.max(1, options.topK ?? 3);
  const alpha = options.alpha ?? 0.35;
  const horizon = Math.max(1, options.horizon ?? 4);
  const immediateWeight = clamp01(options.immediateWeight ?? 0.35);

  const ruleset = rawRuleset.slice(0, 8).map((value) => (value === 1 ? 1 : 0));
  const rows = buildRows(ruleset, cells, generations);

  const encodedStates: EncodedState[] = [];
  let previousDensity = 0;

  for (const row of rows) {
    const encoded = encodeState(row, previousDensity);
    encodedStates.push(encoded);

    const activeCells = row.reduce((sum, value) => sum + (value === 1 ? 1 : 0), 0);
    previousDensity = activeCells / row.length;
  }

  if (encodedStates.length < 2) return null;

  const transitionCounts = new Map<string, Map<string, number>>();
  const stateLabels = new Map<string, string>(STATE_SPACE.map((state) => [state.id, state.label]));

  for (const state of encodedStates) {
    stateLabels.set(state.id, state.label);
  }

  for (let i = 0; i < encodedStates.length - 1; i++) {
    const from = encodedStates[i].id;
    const to = encodedStates[i + 1].id;
    const rowCounts = transitionCounts.get(from) ?? new Map<string, number>();
    rowCounts.set(to, (rowCounts.get(to) ?? 0) + 1);
    transitionCounts.set(from, rowCounts);
  }

  const universe = STATE_SPACE.map((state) => state.id);
  const stateIndex = new Map(universe.map((stateId, index) => [stateId, index]));
  const stateCount = universe.length;

  if (stateCount === 0) return null;

  const toTotals = Array(stateCount).fill(0);
  for (const [, rowCounts] of transitionCounts) {
    for (const [toState, count] of rowCounts) {
      const idx = stateIndex.get(toState);
      if (idx !== undefined) {
        toTotals[idx] += count;
      }
    }
  }

  const toTotalSum = toTotals.reduce((sum, count) => sum + count, 0);
  const prior =
    toTotalSum > 0
      ? toTotals.map((count) => (count + alpha) / (toTotalSum + alpha * stateCount))
      : Array(stateCount).fill(1 / stateCount);

  const transitionMatrix = Array.from({ length: stateCount }, (_, rowIndex) => {
    const fromState = universe[rowIndex];
    const rowCounts = transitionCounts.get(fromState);
    if (!rowCounts) {
      return prior;
    }

    const denominator = universe.reduce((sum, toState) => sum + (rowCounts.get(toState) ?? 0) + alpha, 0);
    if (denominator <= 0) {
      return prior;
    }

    return universe.map((toState) => ((rowCounts.get(toState) ?? 0) + alpha) / denominator);
  });

  const currentState = encodedStates[encodedStates.length - 1];
  const currentStateIndex = stateIndex.get(currentState.id);
  if (currentStateIndex === undefined) {
    return null;
  }

  const outgoing = transitionCounts.get(currentState.id);
  const sampleSize = outgoing
    ? Array.from(outgoing.values()).reduce((sum, count) => sum + count, 0)
    : 0;

  const unit = Array(stateCount).fill(0);
  unit[currentStateIndex] = 1;

  const oneStep = multiplyVectorByMatrix(unit, transitionMatrix);
  let horizonStep = oneStep;
  for (let step = 1; step < horizon; step++) {
    horizonStep = multiplyVectorByMatrix(horizonStep, transitionMatrix);
  }

  const blended = normalize(
    oneStep.map((value, index) => value * immediateWeight + horizonStep[index] * (1 - immediateWeight)),
  );

  const predictions = universe
    .map((stateId, index) => {
      const count = outgoing?.get(stateId) ?? 0;
      const probability = clamp01(blended[index] ?? 0);
      return {
        id: stateId,
        label: stateLabels.get(stateId) ?? stateId,
        probability,
        count,
      };
    })
    .sort((a, b) => b.probability - a.probability)
    .slice(0, topK);

  return {
    currentStateId: currentState.id,
    currentStateLabel: currentState.label,
    sampleSize,
    predictions,
  };
};
